// src/pages/admin/AdminBookings.jsx — FINAL (SAFE)
// + Commute Habits management (admin can reset/delete per-user commute)
// + Commute in cards + CSV/PDF exports + Commute report summary
// + Parking + Motorcycle + Desk (no breaking changes)
// + Fallback: if /admin/commutes endpoint not available, derive commute habits from bookings (read-only)
// + NEW (safe): Admin can "Cancel" booking (soft-cancel) to free resources (fallback endpoint)
// + FIX: Commute report counts per booking in selected period
// + FIX (per screenshot): DO NOT create "Unknown" distance column.
//        If Estimated Distance is empty, user still counted in TOTAL only (distance buckets stay blank).

import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import Card from '../../components/Card.jsx';
import Button from '../../components/Button.jsx';
import Input from '../../components/Input.jsx';
import LoadingSpinner from '../../components/LoadingSpinner.jsx';
import ConfirmDialog from '../../components/ConfirmDialog.jsx';
import {
  FileText,
  Download,
  Calendar,
  Users,
  Car,
  Monitor,
  Filter,
  Trash2,
  CheckSquare,
  Square,
  Bike,
  Undo2,
  Unlock,
  Ban,
} from 'lucide-react';
import { format, parseISO, startOfDay, endOfDay } from 'date-fns';
import { api } from '../../lib/api.js';

/* ===================== Options ===================== */
const COMMUTE_OPTIONS = [
  'Walk',
  'Bicycle',
  'E-Bike',
  'Motorcycle',
  'Motorcycle with a colleague',
  'Car',
  'Car with a colleague',
  'Public transport',
];

const DISTANCE_OPTIONS = ['0-5 km', '5-10 km', '10-20 km', '20 - 30 km', '30+ km'];

/* ---------- time helpers ---------- */
const toLocalDate = (s) => {
  if (!s) return null;
  try {
    return parseISO(String(s).replace(' ', 'T'));
  } catch {
    return null;
  }
};
const utcStringToLocalDate = (s) => {
  if (!s) return null;
  try {
    const base = String(s).includes('T') ? String(s) : String(s).replace(' ', 'T');
    return parseISO(base + 'Z');
  } catch {
    return null;
  }
};
const getStartDate = (b) =>
  b.start_local ? toLocalDate(b.start_local) : utcStringToLocalDate(b.start_time);
const getEndDate = (b) => (b.end_local ? toLocalDate(b.end_local) : utcStringToLocalDate(b.end_time));
const getCreated = (b) => (b.created_at ? utcStringToLocalDate(b.created_at) : null);

/* ---------- derived status (live) ---------- */
const deriveStatus = (b) => {
  const raw = String(b.status || '').toLowerCase();
  if (raw === 'cancelled') return 'cancelled';
  if (raw === 'released') return 'released';
  const end = getEndDate(b);
  if (end && end <= new Date()) return 'completed';
  return 'active';
};
const statusLabel = (s) => (s === 'completed' ? 'Done' : s.charAt(0).toUpperCase() + s.slice(1));

/* ---------- normalize helpers ---------- */
const norm = (v) => String(v ?? '').trim();
const normLower = (v) => norm(v).toLowerCase();

/* ---------- commute helpers ---------- */
const getCommuteValue = (b) => {
  const direct =
    norm(b.commute) ||
    norm(b.commute_habit) ||
    norm(b.commuteHabit) ||
    norm(b.commute_habbit) ||
    norm(b.commuteHabbit);

  if (direct) return direct;

  const maybeNotes = norm(b.notes);
  if (!maybeNotes) return '';

  const hit = COMMUTE_OPTIONS.find((x) => x.toLowerCase() === maybeNotes.toLowerCase());
  return hit || '';
};

/* ---------- distance helpers ---------- */
const getDistanceValue = (b) => {
  const direct =
    norm(b.estimated_distance) ||
    norm(b.estimatedDistance) ||
    norm(b.distance) ||
    norm(b.distance_bucket) ||
    norm(b.distanceBucket);

  if (!direct) return '';

  const hit = DISTANCE_OPTIONS.find((x) => x.toLowerCase() === direct.toLowerCase());
  return hit || direct;
};

const getBookingTsForReport = (b) => {
  const e = getEndDate(b);
  if (e && !isNaN(e)) return e.getTime();
  const s = getStartDate(b);
  if (s && !isNaN(s)) return s.getTime();
  const c = getCreated(b);
  if (c && !isNaN(c)) return c.getTime();
  return 0;
};

function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [userMap, setUserMap] = useState({});
  const [loading, setLoading] = useState(true);

  // tick for live status update every minute
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const tm = setInterval(() => setTick((t) => t + 1), 60000);
    return () => clearInterval(tm);
  }, []);

  const [deleteDialog, setDeleteDialog] = useState({ open: false, booking: null, loading: false });
  const [batchDialog, setBatchDialog] = useState({ open: false, loading: false });
  const [selected, setSelected] = useState(() => new Set());

  // cancel booking dialog (soft cancel)
  const [cancelDialog, setCancelDialog] = useState({ open: false, booking: null, loading: false });
  const [releaseDialog, setReleaseDialog] = useState({ open: false, booking: null, loading: false });

  // commute reset dialog
  const [commuteResetDialog, setCommuteResetDialog] = useState({
    open: false,
    row: null,
    loading: false,
  });

  const [filters, setFilters] = useState({
    startDate: format(new Date(), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd'),
    type: 'all', // all | parking | motorcycle | desk | commute
    status: 'all', // all | active | completed | cancelled
  });

  // Commute Habits list
  const [commuteLoading, setCommuteLoading] = useState(true);
  const [commuteEndpointOk, setCommuteEndpointOk] = useState(true);
  const [commuteRows, setCommuteRows] = useState([]); // [{user_id, username, commute, last_seen}]
  const [commuteFilter, setCommuteFilter] = useState({ q: '', commute: 'all' });

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [bRes, uRes] = await Promise.all([api.get('/bookings'), api.get('/admin/users')]);

      const map = {};
      (uRes.data || []).forEach((u) => {
        map[u.id] = u.username || '';
      });
      setUserMap(map);

      const list = (bRes.data || []).map((b) => ({
        ...b,
        username: map[b.user_id] || b.username || '-',
        commute_value: getCommuteValue(b),
        distance_value: getDistanceValue(b),
      }));
      setBookings(list);

      await fetchCommuteHabits({ bookingsFallback: list, userMapFallback: map });
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.error || 'Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const fetchCommuteHabits = async ({ bookingsFallback, userMapFallback } = {}) => {
    setCommuteLoading(true);

    const list = Array.isArray(bookingsFallback) ? bookingsFallback : bookings;
    const umap = userMapFallback || userMap || {};

    try {
      const r = await api.get('/admin/commutes');
      const rows = Array.isArray(r.data) ? r.data : [];

      const normalized = rows
        .map((x) => {
          const uid = x.user_id ?? x.userId ?? x.id;
          return {
            user_id: uid,
            username: x.username || umap[uid] || '',
            commute: norm(x.commute),
            last_seen: x.last_seen || x.updated_at || x.created_at || null,
          };
        })
        .filter((x) => x.user_id && x.commute);

      setCommuteRows(normalized);
      setCommuteEndpointOk(true);
    } catch (e) {
      // fallback: latest commute per user derived from bookings (read-only)
      setCommuteEndpointOk(false);

      const byUser = new Map();
      for (const b of list) {
        const cv = getCommuteValue(b);
        if (!cv) continue;
        const uid = b.user_id;
        if (!uid) continue;

        const ts = getBookingTsForReport(b);
        const prev = byUser.get(uid);
        if (!prev || ts >= prev.ts) {
          byUser.set(uid, {
            user_id: uid,
            username: umap[uid] || b.username || '-',
            commute: cv,
            last_seen: (getEndDate(b) || getStartDate(b) || getCreated(b))?.toISOString?.() || null,
            ts,
          });
        }
      }

      const derived = Array.from(byUser.values()).sort((a, b) =>
        String(a.username).localeCompare(String(b.username))
      );
      setCommuteRows(derived);
    } finally {
      setCommuteLoading(false);
    }
  };

  const applyFilters = (list) => {
    const startBound = startOfDay(parseISO(filters.startDate));
    const endBound = endOfDay(parseISO(filters.endDate));

    return (list || []).filter((b) => {
      const dt = getStartDate(b);
      if (!dt) return false;
      if (dt < startBound || dt > endBound) return false;

      const t = String(b.type_name || '').toLowerCase();
      if (filters.type !== 'all' && filters.type !== 'commute' && t !== filters.type) return false;

      const dStatus = deriveStatus(b);
      if (filters.status !== 'all' && dStatus !== filters.status) return false;

      return true;
    });
  };

  const filtered = useMemo(() => applyFilters(bookings), [bookings, filters, tick]);
  const showCommuteOnly = filters.type === 'commute';

      /* ============================================================
     Commute Habit Report (Per Booking) - MATRIX WITHOUT "Unknown"
     - Each booking counted (no per-user or per-day de-dup)
     - If distance empty/unmatched => NOT counted in any distance column,
       but still counted in TOTAL (matches spreadsheet style).
  ============================================================ */
  const commuteDistanceReport = useMemo(() => {
    const items = [];

    for (const b of filtered) {
      const commute = getCommuteValue(b);
      if (!commute) continue;

      const distance = getDistanceValue(b) || '';
      items.push({ commute, distance });
    }

    // commutes present (keep COMMUTE_OPTIONS order first)
    const commuteSet = new Set();
    for (const v of items) commuteSet.add(v.commute);

    const commutesOrdered = [
      ...COMMUTE_OPTIONS.filter((c) => commuteSet.has(c)),
      ...Array.from(commuteSet)
        .filter((c) => !COMMUTE_OPTIONS.includes(c))
        .sort((a, b) => a.localeCompare(b)),
    ];

    const distances = [...DISTANCE_OPTIONS];

    // matrix: commute -> array counts
    const rowMap = new Map(); // commute -> number[distances.length]
    const rowTotals = new Map(); // commute -> total bookings for that commute
    const colTotals = new Array(distances.length).fill(0);

    let grandTotal = 0;
    let missingDistanceCount = 0;

    for (const v of items) {
      const commute = v.commute;
      const dist = v.distance;

      if (!rowMap.has(commute)) rowMap.set(commute, new Array(distances.length).fill(0));
      rowTotals.set(commute, (rowTotals.get(commute) || 0) + 1);

      // assign to a distance bucket ONLY if it matches an option
      if (dist) {
        const idx = distances.findIndex((d) => d.toLowerCase() === String(dist).toLowerCase());
        if (idx >= 0) {
          const arr = rowMap.get(commute);
          arr[idx] += 1;
          colTotals[idx] += 1;
        } else {
          // value exists but not matching options -> treat as missing for matrix
          missingDistanceCount += 1;
        }
      } else {
        missingDistanceCount += 1;
      }

      grandTotal += 1;
    }

    const rows = commutesOrdered.map((commute) => {
      const cells = rowMap.get(commute) || new Array(distances.length).fill(0);
      const rowTotal = rowTotals.get(commute) || 0;
      return { commute, cells, rowTotal };
    });

    const totalRow = { cells: colTotals, rowTotal: grandTotal };

    return {
      distances,
      rows,
      totalRow,
      totalBookings: items.length,
      missingDistanceCount,
    };
  }, [filtered]);

  /* ---------- Commute Habits (admin list filtering) ---------- */
  const commuteFilteredRows = useMemo(() => {
    const q = norm(commuteFilter.q).toLowerCase();
    const wanted = commuteFilter.commute;
    return (commuteRows || []).filter((r) => {
      if (wanted !== 'all' && normLower(r.commute) !== normLower(wanted)) return false;
      if (!q) return true;
      const hay = `${r.username || ''} ${r.user_id || ''} ${r.commute || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [commuteRows, commuteFilter]);

  /* ---------- Export ---------- */
  const exportToCSV = () => {
    try {
      if (showCommuteOnly) {
        const matrixTitle = 'Commute Habit Report (Per Booking)\n';
        const cols = ['Commute', ...(commuteDistanceReport.distances || []), 'Total'];
        const matrixHeader = cols.map((x) => '"' + x + '"').join(',') + '\n';

        const matrixRows = (commuteDistanceReport.rows || [])
          .map((r) => {
            const line = [r.commute, ...(r.cells || []), r.rowTotal];
            return line.map((x) => '"' + x + '"').join(',');
          })
          .join('\n');

        const matrixTotalRow =
          '\n' +
          ['Total', ...(commuteDistanceReport.totalRow?.cells || []), commuteDistanceReport.totalRow?.rowTotal || 0]
            .map((x) => '"' + x + '"')
            .join(',');

        const note =
          '\n\nNote: Bookings without Estimated Distance are counted in "Total" only (not assigned to distance buckets).' +
          ' Missing distance count: ' + (commuteDistanceReport.missingDistanceCount || 0) + '\n';

        const blob = new Blob([matrixTitle + matrixHeader + matrixRows + matrixTotalRow + note], {
          type: 'text/csv;charset=utf-8;',
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'commute_habits_report_' + format(new Date(), 'yyyy-MM-dd') + '.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        toast.success('CSV report downloaded.');
        return;
      }

      const header =
        'No,Resource,Type,User,Commute,Estimated Distance,Start Time,End Time,Status,Created At\n';
      const rows = filtered
        .map((b, i) => {
          const s = getStartDate(b),
            e = getEndDate(b),
            c = getCreated(b);
          const dStatus = deriveStatus(b);
          const commute = getCommuteValue(b);
          const dist = getDistanceValue(b);
          return [
            i + 1,
            `"${b.resource_name || ''}"`,
            `"${b.type_name || ''}"`,
            `"${b.username || '-'}"`,
            `"${commute || ''}"`,
            `"${dist || ''}"`,
            `"${s ? format(s, 'yyyy-MM-dd HH:mm') : ''}"`,
            `"${e ? format(e, 'yyyy-MM-dd HH:mm') : ''}"`,
            `"${dStatus}"`,
            `"${c ? format(c, 'yyyy-MM-dd HH:mm') : ''}"`,
          ].join(',');
        })
        .join('\n');

      // Matrix section (NO "Unknown" column)
      const matrixTitle = '\n\nCommute Habit Report (Per Booking)\n';
      const cols = ['Commute', ...(commuteDistanceReport.distances || []), 'Total'];
      const matrixHeader = cols.map((x) => `"${x}"`).join(',') + '\n';

      const matrixRows = (commuteDistanceReport.rows || [])
        .map((r) => {
          const line = [r.commute, ...(r.cells || []), r.rowTotal];
          return line.map((x) => `"${x}"`).join(',');
        })
        .join('\n');

      const matrixTotalRow =
        '\n' +
        ['Total', ...(commuteDistanceReport.totalRow?.cells || []), commuteDistanceReport.totalRow?.rowTotal || 0]
          .map((x) => `"${x}"`)
          .join(',');

      const note =
        '\n\nNote: Bookings without Estimated Distance are counted in "Total" only (not assigned to distance buckets).' +
        ' Missing distance count: ' + (commuteDistanceReport.missingDistanceCount || 0) + '\n';

      const blob = new Blob([header + rows + matrixTitle + matrixHeader + matrixRows + matrixTotalRow + note], {
        type: 'text/csv;charset=utf-8;',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bookings_report_${format(new Date(), 'yyyy-MM-dd')}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success('CSV report downloaded.');
    } catch (e) {
      console.error(e);
      toast.error('Failed to export CSV.');
    }
  };

  const exportToPDF = () => {
    try {
      const distances = commuteDistanceReport.distances || [];

      const matrixHtml =
        commuteDistanceReport.rows?.length
          ? `
          <h2 style="margin-top:18px;">Commute Habit Report (Per Booking)</h2>
          <div style="margin-top:6px; color:#333;">
            Total bookings in period: <b>${commuteDistanceReport.totalBookings || 0}</b>
            ${commuteDistanceReport.missingDistanceCount ? ` - Missing distance: <b>${commuteDistanceReport.missingDistanceCount}</b>` : ''}
          </div>

          <table style="margin-top:12px;">
            <thead>
              <tr>
                <th style="text-align:left;">Commute</th>
                ${distances.map((d) => `<th>${d}</th>`).join('')}
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              ${(commuteDistanceReport.rows || [])
                .map(
                  (r) => `
                    <tr>
                      <td style="text-align:left;">${r.commute}</td>
                      ${(r.cells || []).map((n) => `<td>${n || ''}</td>`).join('')}
                      <td><b>${r.rowTotal}</b></td>
                    </tr>
                  `
                )
                .join('')}
              <tr>
                <td style="text-align:left;"><b>Total</b></td>
                ${(commuteDistanceReport.totalRow?.cells || []).map((n) => `<td><b>${n || ''}</b></td>`).join('')}
                <td><b>${commuteDistanceReport.totalRow?.rowTotal || 0}</b></td>
              </tr>
            </tbody>
          </table>

          <div style="margin-top:8px; color:#555; font-size:12px;">
            Note: Bookings without Estimated Distance are counted in <b>Total</b> only (not assigned to distance buckets).
          </div>
        `
          : `<div style="margin-top:18px; color:#555;">No commute data in this period.</div>`;

      if (showCommuteOnly) {
        const html = `
        <html><head>
          <title>Commute Habits Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 24px; }
            h1 { text-align: center; margin-bottom: 16px; }
            h2 { margin: 0; padding: 0; }
            table { width: 100%; border-collapse: collapse; margin-top: 12px; }
            th, td { border: 1px solid #ddd; padding: 10px; font-size: 12px; text-align: center; }
            th { background: #f2f2f2; }
            .meta { margin-top: 6px; color: #333; }
          </style>
        </head><body>
          <h1>Spacebook - Commute Habits Report</h1>
          <div class="meta">Report Date: ${format(new Date(), 'MMMM dd, yyyy')}</div>
          <div class="meta">Period: ${format(parseISO(filters.startDate), 'MMM dd, yyyy')} - ${format(
          parseISO(filters.endDate),
          'MMM dd, yyyy'
        )}</div>
          ${matrixHtml}
        </body></html>`;

        const w = window.open('', '_blank');
        w.document.write(html);
        w.document.close();
        setTimeout(() => w.print(), 400);
        toast.success('PDF opened.');
        return;
      }

      const html = `
        <html><head>
          <title>Bookings Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 24px; }
            h1 { text-align: center; margin-bottom: 16px; }
            h2 { margin: 0; padding: 0; }
            table { width: 100%; border-collapse: collapse; margin-top: 12px; }
            th, td { border: 1px solid #ddd; padding: 10px; font-size: 12px; text-align: center; }
            th { background: #f2f2f2; }
            .meta { margin-top: 6px; color: #333; }
          </style>
        </head><body>
          <h1>Spacebook - Bookings Report</h1>
          <div class="meta">Report Date: ${format(new Date(), 'MMMM dd, yyyy')}</div>
          <div class="meta">Period: ${format(parseISO(filters.startDate), 'MMM dd, yyyy')} - ${format(
        parseISO(filters.endDate),
        'MMM dd, yyyy'
      )}</div>
          <div class="meta">Total Bookings: ${filtered.length}</div>

          <table>
            <thead>
              <tr>
                <th>No.</th><th>Resource</th><th>Type</th><th>User</th><th>Commute</th><th>Estimated Distance</th>
                <th>Start</th><th>End</th><th>Status</th><th>Created At</th>
              </tr>
            </thead>
            <tbody>
              ${filtered
                .map((b, i) => {
                  const s = getStartDate(b),
                    e = getEndDate(b),
                    c = getCreated(b);
                  const dStatus = statusLabel(deriveStatus(b));
                  const commute = getCommuteValue(b);
                  const dist = getDistanceValue(b);
                  return `
                <tr>
                  <td>${i + 1}</td>
                  <td>${b.resource_name || ''}</td>
                  <td>${b.type_name || ''}</td>
                  <td>${b.username || '-'}</td>
                  <td>${commute || ''}</td>
                  <td>${dist || ''}</td>
                  <td>${s ? format(s, 'yyyy-MM-dd HH:mm') : ''}</td>
                  <td>${e ? format(e, 'yyyy-MM-dd HH:mm') : ''}</td>
                  <td>${dStatus}</td>
                  <td>${c ? format(c, 'yyyy-MM-dd HH:mm') : ''}</td>
                </tr>`;
                })
                .join('')}
            </tbody>
          </table>

          ${matrixHtml}
        </body></html>`;

      const w = window.open('', '_blank');
      w.document.write(html);
      w.document.close();
      setTimeout(() => w.print(), 400);
      toast.success('PDF opened.');
    } catch (e) {
      console.error(e);
      toast.error('Failed to export PDF.');
    }
  };
/* ---------- Single hard delete booking ---------- */
  const askHardDelete = (booking) => setDeleteDialog({ open: true, booking, loading: false });
  const doHardDelete = async () => {
    const b = deleteDialog.booking;
    if (!b) return;
    try {
      setDeleteDialog((d) => ({ ...d, loading: true }));
      await api.delete(`/admin/bookings/${b.id}?hard=1`);
      toast.success('Booking deleted permanently.');
      setDeleteDialog({ open: false, booking: null, loading: false });
      setBookings((list) => list.filter((x) => x.id !== b.id));
      setSelected((sel) => {
        const n = new Set(sel);
        n.delete(b.id);
        return n;
      });
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.error || 'Failed to delete booking');
      setDeleteDialog((d) => ({ ...d, loading: false }));
    }
  };
/* ---------- Cancel booking (soft-cancel) ---------- */
  const askCancel = (booking) => setCancelDialog({ open: true, booking, loading: false });

  const doCancel = async () => {
    const b = cancelDialog.booking;
    if (!b?.id) return;

    try {
      setCancelDialog((d) => ({ ...d, loading: true }));

      try {
        await api.put(`/admin/bookings/${b.id}/cancel`);
      } catch (e1) {
        await api.put(`/admin/bookings/${b.id}`, { status: 'cancelled' });
      }

      toast.success('Booking cancelled.');
      setCancelDialog({ open: false, booking: null, loading: false });

      setBookings((prev) => prev.map((x) => (x.id === b.id ? { ...x, status: 'cancelled' } : x)));
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.error || 'Failed to cancel booking');
      setCancelDialog((d) => ({ ...d, loading: false }));
    }
  };

  /* ---------- Release booking (manual release) ---------- */
  const askRelease = (booking) => setReleaseDialog({ open: true, booking, loading: false });

  const doRelease = async () => {
    const b = releaseDialog.booking;
    if (!b?.id) return;

    try {
      setReleaseDialog((d) => ({ ...d, loading: true }));
      await api.put(`/admin/bookings/${b.id}/release`);
      const nowSql = new Date().toISOString().slice(0, 19).replace('T', ' ');
      toast.success('Booking released.');
      setReleaseDialog({ open: false, booking: null, loading: false });

      setBookings((prev) =>
        prev.map((x) => (x.id === b.id ? { ...x, status: 'released', end_time: nowSql } : x))
      );
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.error || 'Failed to release booking');
      setReleaseDialog((d) => ({ ...d, loading: false }));
    }
  };

  /* ---------- Batch hard delete booking ---------- */
  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const selectAll = () => setSelected(new Set(filtered.map((b) => b.id)));
  const unselectAll = () => setSelected(new Set());
  const askBatchDelete = () => {
    if (selected.size) setBatchDialog({ open: true, loading: false });
  };
  const doBatchDelete = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    setBatchDialog((d) => ({ ...d, loading: true }));

    let ok = 0,
      fail = 0;
    for (const id of ids) {
      try {
        await api.delete(`/admin/bookings/${id}?hard=1`);
        ok++;
      } catch {
        fail++;
      }
    }

    setBookings((list) => list.filter((b) => !selected.has(b.id)));
    setSelected(new Set());
    setBatchDialog({ open: false, loading: false });

    if (ok && !fail) toast.success(`Deleted ${ok} booking(s).`);
    else if (ok && fail) {
      toast.custom((t) => (
        <div className="px-4 py-3 bg-gray-800 text-white rounded-lg shadow border border-gray-700">
          <div className="font-semibold">Partial delete</div>
          <div className="text-sm mt-1">
            {ok} deleted, {fail} failed.
          </div>
          <div className="mt-2 text-right">
            <button
              onClick={() => toast.dismiss(t.id)}
              className="px-3 py-1 rounded bg-white/10 hover:bg-white/20"
            >
              Dismiss
            </button>
          </div>
        </div>
      ));
    } else toast.error('Failed to delete selected bookings.');
  };

  /* ---------- Commute Habits: reset/delete ---------- */
  const askResetCommute = (row) => setCommuteResetDialog({ open: true, row, loading: false });

  const doResetCommute = async () => {
    const row = commuteResetDialog.row;
    if (!row?.user_id) return;

    if (!commuteEndpointOk) {
      toast.error('Commute list is read-only (missing /admin/commutes endpoint).');
      setCommuteResetDialog({ open: false, row: null, loading: false });
      return;
    }

    try {
      setCommuteResetDialog((d) => ({ ...d, loading: true }));
      await api.delete(`/admin/commutes/${row.user_id}`);
      toast.success('Commute preference has been reset.');
      setCommuteResetDialog({ open: false, row: null, loading: false });

      setCommuteRows((prev) => prev.filter((x) => Number(x.user_id) !== Number(row.user_id)));
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.error || 'Failed to reset commute');
      setCommuteResetDialog((d) => ({ ...d, loading: false }));
    }
  };

  /* ---------- UI helpers ---------- */
  const getStatusColor = (s) => {
    switch (s) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'released':
        return 'bg-amber-100 text-amber-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const iconForType = (typeName) => {
    const t = String(typeName || '').toLowerCase();
    if (t === 'parking') return { Icon: Car, wrap: 'bg-amber-600' };
    if (t === 'motorcycle') return { Icon: Bike, wrap: 'bg-sky-600' };
    return { Icon: Monitor, wrap: 'bg-emerald-600' };
  };

  const BookingCard = ({ booking, index }) => {
    void tick;
    const s = getStartDate(booking);
    const e = getEndDate(booking);
    const c = getCreated(booking);
    const checked = selected.has(booking.id);
    const dStatus = deriveStatus(booking);
    const commute = getCommuteValue(booking);
    const dist = getDistanceValue(booking);

    const { Icon, wrap } = iconForType(booking.type_name);
    const canCancel = dStatus === 'active';
    const canRelease =
      dStatus === 'active' && String(booking.type_name || '').toLowerCase() === 'parking';

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: index * 0.05 }}
      >
        <Card className="bg-gradient-to-br from-gray-800 to-gray-900 border-gray-600">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center space-x-3">
              <button
                type="button"
                onClick={() => toggleSelect(booking.id)}
                className={`p-1.5 sm:p-2 rounded-md border ${
                  checked
                    ? 'border-purple-500 bg-purple-500/10'
                    : 'border-gray-600 hover:bg-gray-700/40'
                } mr-1`}
                title={checked ? 'Unselect' : 'Select'}
              >
                {checked ? (
                  <CheckSquare className="h-5 w-5 text-purple-400" />
                ) : (
                  <Square className="h-5 w-5 text-gray-400" />
                )}
              </button>

              <div className={`p-2 ${wrap} rounded-lg`}>
                <Icon className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
              </div>

              <div>
                <h3 className="font-semibold text-white text-[11px] sm:text-lg leading-tight truncate max-w-[90px] sm:max-w-none">{booking.resource_name}</h3>
                <p className="text-gray-400 text-[10px] sm:text-sm truncate max-w-[120px] sm:max-w-none">
                  {booking.type_name}  Booked by {booking.username || '-'}
                </p>
              </div>
            </div>

            <span
              className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(
                dStatus
              )}`}
            >
              {statusLabel(dStatus)}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-3 text-[10px] sm:text-sm">
            <div>
              <p className="text-gray-400 mb-1">Start Time</p>
              <p className="text-white font-medium">{s ? format(s, 'MMM dd, yyyy') : '-'}</p>
              <p className="text-gray-300">{s ? format(s, 'HH:mm') : ''}</p>
            </div>
            <div>
              <p className="text-gray-400 mb-1">End Time</p>
              <p className="text-white font-medium">{e ? format(e, 'MMM dd, yyyy') : '-'}</p>
              <p className="text-gray-300">{e ? format(e, 'HH:mm') : ''}</p>
            </div>
          </div>

          {booking.floor_name && (
            <div className="mt-3 text-sm">
              <p className="text-gray-400">
                Floor: <span className="text-gray-300">{booking.floor_name}</span>
              </p>
            </div>
          )}

          {(commute || dist) ? (
            <div className="mt-3 bg-gray-700/50 rounded-lg p-3 space-y-1">
              {commute ? (
                <p className="text-gray-300 text-sm">
                  <span className="font-medium">Commute:</span> {commute}
                </p>
              ) : null}
              {dist ? (
                <p className="text-gray-300 text-sm">
                  <span className="font-medium">Estimated Distance:</span> {dist}
                </p>
              ) : null}
            </div>
          ) : booking.notes ? (
            <div className="mt-3 bg-gray-700/50 rounded-lg p-3">
              <p className="text-gray-300 text-sm">
                <span className="font-medium">Notes:</span> {booking.notes}
              </p>
            </div>
          ) : null}

          <div className="mt-4 flex items-center justify-between gap-2 flex-wrap">
            <p className="text-gray-400 text-xs">Created: {c ? format(c, 'yyyy-MM-dd HH:mm') : '-'}</p>

            <div className="flex items-center gap-2 ml-auto">
              <Button
                variant="secondary"
                className="flex items-center"
                onClick={() => askRelease(booking)}
                disabled={!canRelease}
                title={!canRelease ? 'Only active parking bookings can be released' : 'Release booking'}
              >
                <Unlock className="h-4 w-4 mr-2" />
                Release
              </Button>

              <Button
                variant="secondary"
                className="flex items-center"
                onClick={() => askCancel(booking)}
                disabled={!canCancel}
                title={!canCancel ? 'Only active bookings can be cancelled' : 'Cancel booking (soft)'}
              >
                <Ban className="h-4 w-4 mr-2" />
                Cancel
              </Button>

              <Button
                variant="danger"
                className="flex items-center"
                onClick={() => askHardDelete(booking)}
                title="Delete permanently"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Permanently
              </Button>
            </div>
          </div>
        </Card>
      </motion.div>
    );
  };

  // stats based on derived status
  const activeCount = bookings.filter((b) => deriveStatus(b) === 'active').length;
  const parkingCount = bookings.filter((b) => String(b.type_name).toLowerCase() === 'parking').length;
  const motorcycleCount = bookings.filter((b) => String(b.type_name).toLowerCase() === 'motorcycle')
    .length;
  const deskCount = bookings.filter((b) => String(b.type_name).toLowerCase() === 'desk').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between flex-wrap gap-3"
      >
        <div>
          <h1 className="text-2xl font-bold text-white mb-2 flex items-center">
            <FileText className="h-8 w-8 text-purple-400 mr-3" />
            Booking & Reports
          </h1>
          <p className="text-gray-400">View all bookings and generate comprehensive reports</p>
        </div>
        <div className="flex space-x-3">
          <Button onClick={exportToCSV} variant="secondary" className="flex items-center">
            <Download className="h-5 w-5 mr-2" />
            Export CSV
          </Button>
          <Button onClick={exportToPDF} className="flex items-center">
            <Download className="h-5 w-5 mr-2" />
            Export PDF
          </Button>
        </div>
      </motion.div>
      {!showCommuteOnly && (
        <>
          {/* Stats */}
          <div className="grid grid-cols-3 md:grid-cols-5 gap-6">
            <Card className="bg-gradient-to-br from-blue-800 to-blue-900">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-200 text-sm">Total Bookings</p>
                  <p className="text-3xl font-bold text-white">{bookings.length}</p>
                </div>
                <Calendar className="h-8 w-8 text-blue-400" />
              </div>
            </Card>

            <Card className="bg-gradient-to-br from-green-800 to-green-900">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-200 text-sm">Active Bookings</p>
                  <p className="text-3xl font-bold text-white">{activeCount}</p>
                </div>
                <Users className="h-8 w-8 text-green-400" />
              </div>
            </Card>

            <Card className="bg-gradient-to-br from-amber-800 to-amber-900">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-amber-200 text-sm">Parking Bookings</p>
                  <p className="text-3xl font-bold text-white">{parkingCount}</p>
                </div>
                <Car className="h-8 w-8 text-amber-400" />
              </div>
            </Card>

            <Card className="bg-gradient-to-br from-sky-800 to-sky-900">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sky-200 text-sm">Motorcycle Bookings</p>
                  <p className="text-3xl font-bold text-white">{motorcycleCount}</p>
                </div>
                <Bike className="h-8 w-8 text-sky-300" />
              </div>
            </Card>

            <Card className="bg-gradient-to-br from-emerald-800 to-emerald-900">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-emerald-200 text-sm">Desk Bookings</p>
                  <p className="text-3xl font-bold text-white">{deskCount}</p>
                </div>
                <Monitor className="h-8 w-8 text-emerald-400" />
              </div>
            </Card>
          </div>
        </>
      )}
      {/* Filters */}
      <Card className="bg-gray-800 border-gray-700">
        <div className="flex items-center space-x-2 mb-4">
          <Filter className="h-5 w-5 text-purple-400" />
          <h3 className="text-lg font-semibold text-white">Filters</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Input
            label="Start Date"
            type="date"
            value={filters.startDate}
            onChange={(e) => setFilters((p) => ({ ...p, startDate: e.target.value }))}
          />
          <Input
            label="End Date"
            type="date"
            value={filters.endDate}
            onChange={(e) => setFilters((p) => ({ ...p, endDate: e.target.value }))}
          />
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Resource Type</label>
            <select
              value={filters.type}
              onChange={(e) => setFilters((p) => ({ ...p, type: e.target.value }))}
              className="block w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="all">All Types</option>
              <option value="parking">Parking</option>
              <option value="motorcycle">Motorcycle</option>
              <option value="desk">Desk</option>
              <option value="commute">Commute Habits Report</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))}
              className="block w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="released">Released</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          {!showCommuteOnly && (
            <div className="flex items-end justify-end space-x-2">
              <Button
                variant="secondary"
                onClick={selected.size === filtered.length ? unselectAll : selectAll}
              >
                {selected.size === filtered.length ? 'Unselect All' : 'Select All'}
              </Button>
              <Button
                variant="danger"
                onClick={askBatchDelete}
                disabled={selected.size === 0}
                title="Delete selected permanently"
              >
                <Trash2 className="h-4 w-4 inline mr-2" />
                Delete Selected ({selected.size})
              </Button>
            </div>
          )}
        </div>
      </Card>

      {/* Commute Habit Report (Matrix) */}
      <Card className="bg-gray-800 border-gray-700">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <h3 className="text-lg font-semibold text-white">Commute Habit Report (Per Booking)</h3>
            <p className="text-gray-400 text-sm">
              Commute × Estimated Distance  {format(parseISO(filters.startDate), 'MMM dd')} -{' '}
              {format(parseISO(filters.endDate), 'MMM dd, yyyy')}
            </p>
          </div>
          <div className="text-gray-300 text-sm text-right">
            <div>
              Total bookings: <span className="text-white font-semibold">{commuteDistanceReport.totalBookings || 0}</span>
            </div>
            {commuteDistanceReport.missingDistanceCount ? (
              <div className="text-xs text-gray-400">
                Missing distance: {commuteDistanceReport.missingDistanceCount}
              </div>
            ) : null}
          </div>
        </div>

        {commuteDistanceReport.rows?.length ? (
          <div className="overflow-auto border border-gray-700 rounded-xl">
            <table className="w-full text-sm">
              <thead className="bg-gray-900/60 text-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 whitespace-nowrap">Commute</th>
                  {(commuteDistanceReport.distances || []).map((d) => (
                    <th key={d} className="text-center px-4 py-3 whitespace-nowrap">
                      {d}
                    </th>
                  ))}
                  <th className="text-center px-4 py-3 whitespace-nowrap">Total</th>
                </tr>
              </thead>
              <tbody className="bg-gray-800">
                {(commuteDistanceReport.rows || []).map((r) => (
                  <tr key={r.commute} className="border-t border-gray-700">
                    <td className="px-4 py-3 text-gray-100 font-medium whitespace-nowrap">{r.commute}</td>
                    {(r.cells || []).map((n, idx) => (
                      <td key={`${r.commute}-${idx}`} className="px-4 py-3 text-center text-gray-200">
                        {n ? n : ''}
                      </td>
                    ))}
                    <td className="px-4 py-3 text-center text-white font-semibold">{r.rowTotal}</td>
                  </tr>
                ))}

                <tr className="border-t border-gray-700 bg-gray-900/40">
                  <td className="px-4 py-3 text-gray-100 font-semibold whitespace-nowrap">Total</td>
                  {(commuteDistanceReport.totalRow?.cells || []).map((n, idx) => (
                    <td key={`total-${idx}`} className="px-4 py-3 text-center text-gray-100 font-semibold">
                      {n ? n : ''}
                    </td>
                  ))}
                  <td className="px-4 py-3 text-center text-white font-bold">
                    {commuteDistanceReport.totalRow?.rowTotal || 0}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-gray-400">No commute data in this period.</div>
        )}

        <div className="mt-2 text-xs text-gray-400">
          Note: If Estimated Distance is empty, the user is counted in <b className="text-gray-200">Total</b> only (distance buckets stay blank).
        </div>
      </Card>
      {!showCommuteOnly && (
        <>
          {/* Summary */}
          <div className="flex items-center justify-between text-gray-300 flex-wrap gap-2">
            <p>
              Showing {filtered.length} of {bookings.length} bookings
            </p>
            <p className="text-sm">
              Period: {format(parseISO(filters.startDate), 'MMM dd')} -{' '}
              {format(parseISO(filters.endDate), 'MMM dd, yyyy')}
            </p>
          </div>

          {/* List */}
          {filtered.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {filtered.map((b, i) => (
                <BookingCard key={b.id} booking={b} index={i} />
              ))}
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-12"
            >
              <div className="w-24 h-24 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-6">
                <FileText className="h-12 w-12 text-gray-500" />
              </div>
              <h3 className="text-xl font-medium text-white mb-2">No bookings found</h3>
              <p className="text-gray-400">Try adjusting the date range or filters.</p>
            </motion.div>
          )}
        </>
      )}

      {/* Commute Habits Management */}
      <Card className="bg-gray-800 border-gray-700">
        <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
          <div>
            <h3 className="text-lg font-semibold text-white">Commute Habits</h3>
            <p className="text-gray-400 text-sm">
              Manage saved commute preferences per user (reset to allow user choose again).
              {!commuteEndpointOk && (
                <span className="ml-2 text-amber-300">
                  (Read-only fallback: missing /admin/commutes endpoint)
                </span>
              )}
            </p>
          </div>

          <div className="flex gap-2">
            <Button
              variant="secondary"
              onClick={() => fetchCommuteHabits({ bookingsFallback: bookings, userMapFallback: userMap })}
              className="whitespace-nowrap"
            >
              Refresh
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <Input
            label="Search"
            value={commuteFilter.q}
            onChange={(e) => setCommuteFilter((p) => ({ ...p, q: e.target.value }))}
            placeholder="Search username / user id / commute..."
          />

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Commute</label>
            <select
              value={commuteFilter.commute}
              onChange={(e) => setCommuteFilter((p) => ({ ...p, commute: e.target.value }))}
              className="block w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="all">All</option>
              {COMMUTE_OPTIONS.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-end justify-end">
            <div className="text-gray-400 text-sm">
              Total: <span className="text-gray-200 font-semibold">{commuteFilteredRows.length}</span>
            </div>
          </div>
        </div>

        {commuteLoading ? (
          <div className="flex items-center justify-center py-10">
            <LoadingSpinner size="lg" />
          </div>
        ) : commuteFilteredRows.length ? (
          <div className="overflow-auto border border-gray-700 rounded-xl">
            <table className="w-full text-sm">
              <thead className="bg-gray-900/60 text-gray-200">
                <tr>
                  <th className="text-left px-4 py-3">User</th>
                  <th className="text-left px-4 py-3">Commute</th>
                  <th className="text-left px-4 py-3">Last Seen</th>
                  <th className="text-right px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody className="bg-gray-800">
                {commuteFilteredRows.map((r) => {
                  const lastSeen = r.last_seen
                    ? (() => {
                        try {
                          const d = String(r.last_seen).includes('T')
                            ? new Date(r.last_seen)
                            : new Date(String(r.last_seen).replace(' ', 'T') + 'Z');
                          if (!isNaN(d)) return format(d, 'yyyy-MM-dd HH:mm');
                        } catch {}
                        return String(r.last_seen);
                      })()
                    : '-';

                  return (
                    <tr key={`${r.user_id}-${r.commute}`} className="border-t border-gray-700">
                      <td className="px-4 py-3">
                        <div className="text-gray-100 font-medium">{r.username || '-'}</div>
                        <div className="text-gray-400 text-xs">ID: {r.user_id}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-200">{r.commute}</td>
                      <td className="px-4 py-3 text-gray-300">{lastSeen}</td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          variant="secondary"
                          className={`inline-flex items-center ${
                            !commuteEndpointOk ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                          onClick={() => commuteEndpointOk && askResetCommute(r)}
                          disabled={!commuteEndpointOk}
                          title={!commuteEndpointOk ? 'Endpoint missing' : 'Reset commute for this user'}
                        >
                          <Undo2 className="h-4 w-4 mr-2" />
                          Reset
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-gray-400">No commute habits found.</div>
        )}
      </Card>

      {/* Release booking confirm */}
      <ConfirmDialog
        isOpen={releaseDialog.open}
        onClose={() => setReleaseDialog({ open: false, booking: null, loading: false })}
        onConfirm={doRelease}
        title="Release Booking"
        message={`Release booking "${releaseDialog.booking?.resource_name}" by ${
          releaseDialog.booking?.username || '-'
        }? This will free the slot for others today.`}
        type="danger"
        confirmText="Release"
        loading={releaseDialog.loading}
      />

      {/* Cancel booking confirm */}
      <ConfirmDialog
        isOpen={cancelDialog.open}
        onClose={() => setCancelDialog({ open: false, booking: null, loading: false })}
        onConfirm={doCancel}
        title="Cancel Booking"
        message={`Cancel booking "${cancelDialog.booking?.resource_name}" by ${
          cancelDialog.booking?.username || '-'
        }? This will free the resource immediately (soft cancel).`}
        type="danger"
        confirmText="Cancel Booking"
        loading={cancelDialog.loading}
      />

      {/* Single delete confirm */}
      <ConfirmDialog
        isOpen={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, booking: null, loading: false })}
        onConfirm={doHardDelete}
        title="Delete Booking Permanently"
        message={`Permanently delete booking "${deleteDialog.booking?.resource_name}" by ${
          deleteDialog.booking?.username || '-'
        }? This cannot be undone.`}
        type="danger"
        confirmText="Delete Permanently"
        loading={deleteDialog.loading}
      />

      {/* Batch delete confirm */}
      <ConfirmDialog
        isOpen={batchDialog.open}
        onClose={() => setBatchDialog({ open: false, loading: false })}
        onConfirm={doBatchDelete}
        title="Delete Selected Bookings"
        message={`Permanently delete ${selected.size} booking(s)? This cannot be undone.`}
        type="danger"
        confirmText={`Delete ${selected.size} Booking(s)`}
        loading={batchDialog.loading}
      />

      {/* Reset commute confirm */}
      <ConfirmDialog
        isOpen={commuteResetDialog.open}
        onClose={() => setCommuteResetDialog({ open: false, row: null, loading: false })}
        onConfirm={doResetCommute}
        title="Reset Commute Preference"
        message={`Reset commute for "${commuteResetDialog.row?.username || '-'}" (ID: ${
          commuteResetDialog.row?.user_id || '-'
        })? User will be able to choose again on next booking.`}
        type="danger"
        confirmText="Reset"
        loading={commuteResetDialog.loading}
      />
    </div>
  );
}

export default AdminBookings;

























